<template>
  <div
    id="datepicker-inline"
    ref="datepickerRef"
    inline-datepicker
    @changeDate="handleDateChange"
    @changeMonth="handleMonthChange"
  ></div>
</template>

<script setup lang="ts">
import type { DatepickerInterface, DatepickerOptions } from "flowbite";
import type { InstanceOptions } from "flowbite";
import { Datepicker } from "flowbite";
import moment from "moment-timezone";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const emit = defineEmits(["update:modelValue", "month-change"]);
const datepickerRef = ref<HTMLDivElement | null>(null);
const datepicker = ref<DatepickerInterface | null>(null);
const props = withDefaults(
  defineProps<{
    disabledDates?: Date[];
    minDate?: string | Date | undefined | null;
    modelValue: string | Date | number;
  }>(),
  { minDate: Date.toString(), disabledDates: () => [] }
);

onMounted(() => {
  const options: DatepickerOptions = {
    defaultDatepickerId: null,
    autohide: false,
    format: "mm/dd/yyyy",
    maxDate: null,
    minDate: props.minDate ? moment(props.minDate).format("MM/DD/YYYY") : null,
    orientation: "bottom",
    buttons: false,
    autoSelectToday: 0,
    title: null,
    rangePicker: false
  };
  const instanceOptions: InstanceOptions = {
    id: "datepicker-custom-example",
    override: true
  };

  datepicker.value = new Datepicker(
    datepickerRef.value,
    options,
    instanceOptions
  );
  const datepickerInstance = datepicker.value.getDatepickerInstance();
  datepickerInstance.setOptions({
    datesDisabled: props.disabledDates
  });

  // set the date (or dates if date range picker)
  datepicker.value.setDate(moment(props.modelValue).format("MM/DD/YYYY"));

  // programatically show the datepicker
  datepicker.value.show();
});

onBeforeUnmount(() => {
  datepicker.value?.destroy();
});

const sameUtcDay = (first: any, second: any) => {
  const firstDate = new Date(first);
  const secondDate = new Date(second);
  return (
    firstDate.getUTCFullYear() === secondDate.getUTCFullYear() &&
    firstDate.getUTCMonth() === secondDate.getUTCMonth() &&
    firstDate.getUTCDate() === secondDate.getUTCDate()
  );
};

// Convert a LOCAL picked day (from Flowbite) -> UTC-noon Date for that same calendar day
const localPickedToUtcNoon = (picked: Date) =>
  new Date(
    Date.UTC(
      picked.getFullYear(),
      picked.getMonth(),
      picked.getDate(),
      12,
      0,
      0,
      0
    )
  );

const handleDateChange = (e) => {
  const picked = e.detail.date as Date;
  const nextUtcNoon = localPickedToUtcNoon(picked);

  if (sameUtcDay(nextUtcNoon, props.modelValue)) return;

  emit("update:modelValue", nextUtcNoon);
};

const handleMonthChange = (e) => {
  if (
    moment(e.detail.viewDate)
      .utc()
      .isSame(moment(props.modelValue).utc(), "month")
  )
    return;
  const month = moment(e.detail.viewDate).month() + 1;
  emit("month-change", month, e.detail.viewDate);
};

watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    if (!moment(newVal).isSame(moment(oldVal), "day")) {
      if (newVal) {
        datepicker.value?.setDate(
          moment(newVal).utc().hours(12).format("MM/DD/YYYY")
        );
      } else {
        datepicker.value?.setDate(undefined);
      }
    }
  }
);
</script>

<style lang="css">
.datepicker-cell.disabled {
  @apply bg-gray-100 !text-gray-400 !rounded-none;
}
.datepicker {
  .datepicker-picker,
  .days,
  .datepicker-grid {
    @apply !w-full min-w-[250px];
  }
  .datepicker-picker {
    @apply !shadow-none;
  }
  .datepicker-cell {
    @apply font-semibold;
  }
}
.datepicker-cell.prev,
.datepicker-cell.next {
  @apply !text-gray-400;
}
</style>
